<template>
  <div class="settings-section" data-test-id="session-objectives-settings">
    <p class="settings-section__title" data-test-id="session-objectives-settings-title">Objectifs de session</p>
    <p class="settings-section__description">
      Définissez les objectifs pédagogiques pour les sessions de ce programme.
    </p>

    <div v-if="isLoadingRef" class="loading">Chargement...</div>

    <div v-else class="objectives-list">
      <div
        v-for="(objective, displayIndex) in visibleObjectives"
        :key="objective.tempId || objective.id"
        class="objective-item"
      >
        <div class="objective-item__header">
          <span class="objective-number">Objectif {{ displayIndex + 1 }}</span>
          <CustomButton
            usage="button"
            type="button"
            color="danger"
            size="small"
            text="Supprimer"
            @emit:click="removeObjective(objective)"
          />
        </div>

        <CustomInput
          type="text"
          :id="`objective-title-${displayIndex}`"
          :data-test-id="`objective-title-${displayIndex}`"
          label="Titre de l'objectif"
          placeholder="Ex: Être commerçant – accueillir, écouter, orienter"
          @emit:change="(value: string) => updateObjective(objective, 'title', value)"
          :value="objective.title"
        />

        <div class="objective-description">
          <p :id="`objective-desc-label-${displayIndex}`" class="input-label">Description</p>
          <TiptapEditor
            :model-value="objective.description || ''"
            placeholder="Décrivez les compétences et connaissances visées..."
            :aria-labelledby="`objective-desc-label-${displayIndex}`"
            :data-test-id="`objective-description-${displayIndex}`"
            @update:modelValue="(value: string) => updateObjectiveDescription(objective, value)"
          />
        </div>

        <CustomInput
          type="number"
          :id="`objective-expected-count-${displayIndex}`"
          :data-test-id="`objective-expected-count-${displayIndex}`"
          label="Nombre de sessions attendues"
          placeholder="1"
          @emit:change="(value: string) => updateObjective(objective, 'expectedSessionCount', parseInt(value) || 1)"
          :value="objective.expectedSessionCount?.toString() || '1'"
        />

        <CustomTextarea
          :id="`objective-mentor-report-template-${displayIndex}`"
          :data-test-id="`objective-mentor-report-template-${displayIndex}`"
          label="Template de compte rendu (mentor)"
          placeholder="Laissez vide pour utiliser le template du programme"
          @emit:change="(value: string) => updateObjective(objective, 'mentorReportTemplate', value)"
          :value="objective.mentorReportTemplate"
        />

        <CustomTextarea
          :id="`objective-mentee-report-template-${displayIndex}`"
          :data-test-id="`objective-mentee-report-template-${displayIndex}`"
          label="Template de compte rendu (mentoré)"
          placeholder="Laissez vide pour utiliser le template du programme"
          @emit:change="(value: string) => updateObjective(objective, 'menteeReportTemplate', value)"
          :value="objective.menteeReportTemplate"
        />
      </div>
    </div>

    <div class="actions">
      <CustomButton
        usage="button"
        type="button"
        color="secondary"
        text="Ajouter un objectif"
        data-test-id="session-objectives-settings-add-objective-button"
        @emit:click="addObjective"
      />

      <CustomButton
        v-if="hasChangesRef"
        usage="button"
        type="button"
        color="primary"
        text="Enregistrer les objectifs"
        :isLoading="isSavingRef"
        data-test-id="session-objectives-settings-save-button"
        @emit:click="saveObjectives"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import { useQuery, useMutation } from '@tanstack/vue-query'

import CustomButton from '@/features/ui/components/button/custom-button.vue'
import CustomInput from '@/features/ui/components/input/custom-input.vue'
import CustomTextarea from '@/features/ui/components/custom-textarea/custom-textarea.vue'
import TiptapEditor from '@/features/ui/components/tiptap-editor/tiptap-editor.vue'
import useToast from '@/hooks/use-toasts.hook'
import {
  getSessionObjectives,
  createSessionObjective,
  updateSessionObjective,
  deleteSessionObjective,
  type SessionObjective,
  SESSION_OBJECTIVES_QUERY_KEY,
} from '@/features/programs/api/session-objectives.api'
import queryClient from '@/lib/vue-query'

interface LocalSessionObjective extends Omit<SessionObjective, 'createdAt' | 'updatedAt'> {
  tempId?: string
  _deleted?: boolean
  _modified?: boolean
}

const props = defineProps<{
  programId: string | null
}>()

const toast = useToast()
const objectivesRef = ref<LocalSessionObjective[]>([])
const initialObjectivesRef = ref<LocalSessionObjective[]>([])
const hasChangesRef = ref(false)
const isSavingRef = ref(false)

// Computed for visible objectives (non-deleted)
const visibleObjectives = computed(() => objectivesRef.value.filter((obj) => !obj._deleted))

// Load objectives
const { data, isLoading: isLoadingRef } = useQuery({
  queryKey: [SESSION_OBJECTIVES_QUERY_KEY, props.programId],
  queryFn: () => {
    if (!props.programId) return Promise.resolve({ objectives: [] })
    return getSessionObjectives(props.programId)
  },
  enabled: computed(() => !!props.programId),
})

// Watch data changes
watch(
  data,
  (newData) => {
    if (newData?.objectives) {
      objectivesRef.value = JSON.parse(JSON.stringify(newData.objectives))
      initialObjectivesRef.value = JSON.parse(JSON.stringify(newData.objectives))
      hasChangesRef.value = false
    }
  },
  { immediate: true },
)

// Watch for changes
watch(
  objectivesRef,
  () => {
    hasChangesRef.value = JSON.stringify(objectivesRef.value) !== JSON.stringify(initialObjectivesRef.value)
  },
  { deep: true },
)

function addObjective() {
  const newObjective: LocalSessionObjective = {
    tempId: uuidv4(),
    title: '',
    description: null,
    mentorReportTemplate: null,
    menteeReportTemplate: null,
    expectedSessionCount: 1,
    order: objectivesRef.value.length,
    programId: props.programId!,
    id: '',
  }
  objectivesRef.value.push(newObjective)
}

function removeObjective(objective: LocalSessionObjective) {
  if (objective.id && !objective.tempId) {
    // Mark for deletion if it exists in DB
    objective._deleted = true
  } else {
    // Remove immediately if it's a new one
    const index = objectivesRef.value.indexOf(objective)
    if (index > -1) {
      objectivesRef.value.splice(index, 1)
    }
  }
  // Re-order remaining visible objectives
  visibleObjectives.value.forEach((obj, idx) => {
    obj.order = idx
  })
}

function updateObjective(objective: LocalSessionObjective, field: string, value: any) {
  if (field === 'title') {
    objective.title = value
  } else if (field === 'description') {
    objective.description = value || null
  } else if (field === 'mentorReportTemplate') {
    objective.mentorReportTemplate = value || null
  } else if (field === 'menteeReportTemplate') {
    objective.menteeReportTemplate = value || null
  } else if (field === 'expectedSessionCount') {
    objective.expectedSessionCount = value
  }
  objective._modified = true
}

function markObjectiveAsModified(objective: LocalSessionObjective) {
  objective._modified = true
}

function updateObjectiveDescription(objective: LocalSessionObjective, value: string) {
  objective.description = value || null
  objective._modified = true
}

async function saveObjectives() {
  if (!props.programId) return

  isSavingRef.value = true
  try {
    const promises = []

    for (const objective of objectivesRef.value) {
      if (objective._deleted && objective.id) {
        // Delete
        promises.push(deleteSessionObjective(objective.id))
      } else if (objective.tempId && !objective.id) {
        // Create new
        const { tempId, _modified, _deleted, id, ...data } = objective
        promises.push(createSessionObjective(data))
      } else if (objective._modified && objective.id) {
        // Update existing
        const { tempId, _modified, _deleted, id, programId, ...data } = objective
        promises.push(updateSessionObjective(id, data))
      }
    }

    await Promise.all(promises)

    // Refresh data
    await queryClient.invalidateQueries({ queryKey: [SESSION_OBJECTIVES_QUERY_KEY, props.programId] })

    toast?.success('Objectifs enregistrés avec succès')
    hasChangesRef.value = false
  } catch (error) {
    console.error('Error saving objectives:', error)
    toast?.error("Erreur lors de l'enregistrement des objectifs")
  } finally {
    isSavingRef.value = false
  }
}
</script>

<style scoped lang="scss">
.settings-section {
  border-top: #2d374830 solid 1px;
  padding: 2rem 2rem 1.5rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.settings-section__title {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
  color: #2d3748;
}

.settings-section__description {
  font-size: 0.875rem;
  color: #718096;
  margin-bottom: 0.5rem;
}

.loading {
  padding: 1rem;
  text-align: center;
  color: #718096;
}

.objectives-list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 1rem;
}

.objective-item {
  width: 100%;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 1.5rem;
  background-color: #f7fafc;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1rem;

  &__header {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
  }

  // Force tous les enfants à prendre toute la largeur
  > * {
    width: 100%;
  }
}

.objective-number {
  font-weight: 600;
  font-size: 1.125rem;
  color: #2d3748;
}

.actions {
  display: flex;
  gap: 1rem;
  align-items: center;
  margin-top: 1rem;
}

.objective-description {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  .input-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #2d3748;
  }

  // Force le fond blanc pour le TiptapEditor
  :deep(.tiptap-editor) {
    background-color: #ffffff !important;
  }

  :deep(.ProseMirror) {
    background-color: #ffffff !important;
  }
}
</style>
